import { useEffect, useMemo, useState } from 'react';

interface UseStickyAboveFooterOptions {
  /**
   * Whether to measure and update. When `false`, the hook attaches no
   * listeners/observers and returns `0` so the caller can short-circuit
   * (e.g. after the bar is dismissed). Default: `true`.
   */
  enabled?: boolean;
  /**
   * One or more CSS selectors for the bottom-of-viewport elements the
   * caller must "lift" above. Defaults to plain `footer` (the single
   * `<Footer />` the app renders today). Pass an array to lift above
   * whichever of several elements is present/tallest — e.g. the page
   * footer AND the fixed mobile bottom toolbar, so a fixed bottom bar
   * clears both. Missing elements contribute `0`.
   */
  selector?: string | string[];
}

/**
 * Returns a pixel offset suitable for a `position: fixed` element
 * pinned to the bottom of the viewport that needs to "lift" above one
 * or more other bottom-anchored elements (the page footer as the user
 * scrolls into it, and/or a fixed mobile bottom toolbar).
 *
 * The offset equals `max(0, window.innerHeight - el.top)` across every
 * matched element — i.e. the height of the visible portion of the
 * tallest overlapping one. When an element is below the fold (or
 * absent) it contributes `0`, so the caller sits flush against the
 * bottom edge.
 *
 * Re-measures on three triggers because a target's position can shift
 * independently of any scroll/resize event:
 *   1. scroll / resize — the obvious cases.
 *   2. `ResizeObserver` on `<body>` + each target — catches async
 *      content loading (PDF page images, score SVGs, lazy-loaded
 *      sections) that pushes the footer further down the document
 *      AFTER initial mount, and the toolbar swapping height between
 *      its normal / edit layouts. Without this the consumer can render
 *      "floating" mid-screen on first paint because the unloaded
 *      page is short and the footer is close to the top.
 *   3. `window 'load'` — final safety net for late-loading
 *      subresources (fonts, images) that `ResizeObserver` might not
 *      see in time.
 *   4. `MutationObserver` on `<body>` — catches a target that mounts
 *      later (e.g. the mobile toolbar appears only once the project is
 *      ready); fixed targets don't resize `<body>`, so (2) can miss it.
 *
 * All measurements are debounced through a single
 * `requestAnimationFrame` so a burst of triggers (e.g. scroll +
 * resize + observer in the same frame) collapses to one read.
 */
export function useStickyAboveFooter({
  enabled = true,
  selector = 'footer',
}: UseStickyAboveFooterOptions = {}): number {
  const [offsetPx, setOffsetPx] = useState(0);

  // Normalise to a stable string key so the effect's dependency doesn't
  // churn when callers pass an inline array literal every render.
  const selectorKey = Array.isArray(selector) ? selector.join(',') : selector;

  const selectors = useMemo(
    () => (Array.isArray(selector) ? selector : [selector]),
    // selectorKey captures the meaningful value; `selector` itself may
    // be a fresh array reference each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectorKey]
  );

  useEffect(() => {
    if (!enabled) {
      setOffsetPx(0);
      return;
    }
    if (typeof window === 'undefined') return;

    const findTargets = (): Element[] =>
      selectors
        .map((sel) => document.querySelector(sel))
        .filter((el): el is Element => el !== null);

    let rafId = 0;
    const measure = () => {
      rafId = 0;
      const overlap = findTargets().reduce((max, el) => {
        const rect = el.getBoundingClientRect();
        return Math.max(max, window.innerHeight - rect.top);
      }, 0);
      const clamped = Math.max(0, overlap);
      setOffsetPx((prev) => (prev === clamped ? prev : clamped));
    };
    const schedule = () => {
      if (rafId !== 0) return;
      rafId = window.requestAnimationFrame(measure);
    };

    measure();

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);

    const observers: ResizeObserver[] = [];
    if (typeof ResizeObserver !== 'undefined') {
      const bodyObserver = new ResizeObserver(schedule);
      bodyObserver.observe(document.body);
      observers.push(bodyObserver);

      findTargets().forEach((el) => {
        const observer = new ResizeObserver(schedule);
        observer.observe(el);
        observers.push(observer);
      });
    }

    // Catch targets that mount AFTER this effect (e.g. the mobile bottom
    // toolbar appears only once the project becomes ready, which happens
    // after this bar renders). Since fixed targets don't resize <body>,
    // the ResizeObservers above may never fire; a childList/subtree
    // MutationObserver re-measures when such a node is added/removed.
    let mutationObserver: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(schedule);
      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('load', schedule);
      observers.forEach((observer) => observer.disconnect());
      mutationObserver?.disconnect();
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
    };
  }, [enabled, selectors]);

  return offsetPx;
}
